import React from 'react';
import { AccountBalance } from '@mui/icons-material';
import ReportLayout from '../Components/Reports/ReportLayout';

interface FRFinancementProps {
  Entete: string;
  /** 'listing' : rapports sur les financements | 'echeancier' : rapports sur les échéances */
  variant?: 'listing' | 'echeancier';
  /** Mode rapport unique : le Type est fixe, pas de select */
  TypeGroup?: string;
}

/** Rapports « listing » (groupe A) — même dataset, seul le regroupement/filtre change */
const REPORTS_LISTING = [
  { value: 'demandeencours',             label: 'Demandes en cours' },
  { value: 'financementpartype',         label: 'Par type de financement' },
  { value: 'financementpargestionnaire', label: 'Par gestionnaire' },
  { value: 'pretparmotif',               label: 'Par motif' },
  { value: 'pratparactivite',            label: 'Par activité' },
  { value: 'pretpargenre',               label: 'Par genre' },
  { value: 'financementparutilisateur',  label: 'Par utilisateur' },
  { value: 'financementparvalideur',     label: 'Par valideur' },
  { value: 'portefeuillefinancement',    label: 'Portefeuille global' },
  { value: 'portefeuillesaint',          label: 'Portefeuille sain' },
  { value: 'portefeuilleimpaye',         label: 'Portefeuille impayés' },
  { value: 'encoursfinancement',         label: 'Encours' },
];

/** Rapports « échéancier » (groupe B) — lignes d'échéances */
const REPORTS_ECHEANCIER = [
  { value: 'previsionpret',         label: 'Prévision des échéances' },
  { value: 'etatremoursementprets', label: 'État des remboursements' },
  { value: 'etatpretechus',         label: 'Financements échus' },
];

/**
 * Composant générique pour TOUS les rapports de financement.
 * - TypeGroup fixe : rapport unique (compatibilité)
 * - variant : interface regroupée avec select « Type de rapport »
 */
const FRFinancement: React.FC<FRFinancementProps> = ({ Entete, variant = 'listing', TypeGroup }) => {
  const merged = !TypeGroup;
  const reportOptions = variant === 'echeancier' ? REPORTS_ECHEANCIER : REPORTS_LISTING;

  return (
    <ReportLayout
      title={Entete}
      subtitle="Module Financements"
      icon={<AccountBalance />}
      endpoint="module-financement"
      extraParams={TypeGroup ? { Type: TypeGroup } : {}}

      showClientRange={false}
      showDateRange={true}
      showAgence={true}
      loadAgenceData={true}

      extraFields={[
        ...(merged ? [{
          key: 'Type',
          label: 'Type de rapport',
          type: 'select' as const,
          options: reportOptions,
          defaultValue: reportOptions[0].value,
        }] : []),

        {
          key: 'CodeTypeFinancement',
          label: 'Type de financement',
          type: 'select' as const,
          fetchUrl: 'typefinancement',
          valueField: 'Code',
          labelField: 'Libelle',
        },
        {
          key: 'CodeTypeProduit',
          label: 'Type de produit',
          type: 'select' as const,
          fetchUrl: 'typeproduitfinancement',
          dependsOn: 'CodeTypeFinancement',
          dependsOnParam: 'CodeTypeFinancement',
          valueField: 'Code',
          labelField: 'Libelle',
        },
        {
          key: 'Statut',
          label: 'Statut',
          type: 'select' as const,
          options: [
            { value: 'ACTIF',   label: 'ACTIF' },
            { value: 'IMPAYE',  label: 'IMPAYE' },
            { value: 'TERMINE', label: 'TERMINE' },
            { value: 'LIVRE',   label: 'LIVRE' },
          ],
        },
        {
          key: 'Gestionnaire', label: 'Gestionnaire', type: 'lookup' as const,
          table: 'UTILIS', colonne1: 'CODUTI', colonne2: 'NOMUTI',
          colagence: '101', lookupChoix: 22,
        },
        {
          key: 'Motif', label: 'Motif', type: 'lookup' as const,
          table: 'LIB', colonne1: 'CODLIB', colonne2: 'LIBELL',
          codeAgence: 'MOTIFPRET', colagence: 'RUBRIQ', lookupChoix: 22,
        },
        {
          key: 'Activite', label: 'Activité', type: 'lookup' as const,
          table: 'TABNAE', colonne1: 'CODNAE', colonne2: 'LIBELL',
          colagence: '101', lookupChoix: 22,
        },
        {
          key: 'Utilisateur', label: 'Utilisateur', type: 'lookup' as const,
          table: 'UTILIS', colonne1: 'CODUTI', colonne2: 'NOMUTI',
          colagence: '101', lookupChoix: 22,
        },
        {
          key: 'NumCli', label: 'Numéro client', type: 'lookup' as const,
          table: 'CLIENT', colonne1: 'NUMCLI', colonne2: 'NOMCLI',
          colagence: '101', lookupChoix: 22,
        },
        { key: 'Min', label: 'Montant min', type: 'number' as const, defaultValue: 0 },
        { key: 'Max', label: 'Montant max', type: 'number' as const, defaultValue: 0 },
      ]}

      fileNamePrefix={`rapport_financement`}
    />
  );
};

export default FRFinancement;
